import { ReactNode } from "react";
import { cx } from "@app/shared/utils/cx";
import styles from "./BaseMarker.module.css";

export type BaseMarkerProps = {
  size?: number;
  color?: string;
  className?: string;
  children?: ReactNode;
};

// Exact geometry from the design spec's SVG (viewBox 0 0 34 37): a circular
// ring with a small pointer tail beneath it. Every derived value below is
// computed from that source SVG's ring `<rect>` (x/y/width/height), not
// invented — keep the two in sync if the spec SVG changes.
const VIEWBOX_WIDTH = 34;
const VIEWBOX_HEIGHT = 37;
const RING_INSET_X = 0.770833;
const RING_INSET_Y = 0.770833;
const RING_SIZE = 32.375;

/**
 * Foundation marker: the empty circular ring + pointer tail from the design
 * spec, in the given `color` (defaults to the branding icon-dark color, so
 * pins track per-tenant branding — see `BrandingColors`/tokens.css).
 * `children` render centered inside the ring — this is how
 * PickupMarker/DropoffMarker/DriverMarker each build their own variant
 * without redrawing the base shape.
 */
export const BaseMarker = ({ size = VIEWBOX_WIDTH, color = "var(--color-icon-dark)", className, children }: BaseMarkerProps) => {
  const height = size * (VIEWBOX_HEIGHT / VIEWBOX_WIDTH);

  return (
    <span className={cx(styles.wrapper, className)} style={{ width: size, height }}>
      <svg
        className={styles.svg}
        width={size}
        height={height}
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M16.9765 36.9971C17.9151 36.9971 20.83 32.7578 24.7096 30.9381L9.29297 30.833C13.7357 33.0461 15.9127 37.1164 16.9765 36.9971Z"
          fill={color}
        />
        <rect x="0.770833" y="0.770833" width="32.375" height="32.375" rx="16.1875" fill="white" />
        <rect x="0.770833" y="0.770833" width="32.375" height="32.375" rx="16.1875" stroke={color} strokeWidth="1.54167" />
      </svg>
      {children ? (
        // Sized and positioned to exactly cover the ring's circular area
        // (as a percentage box, not a fixed pixel one, so it still lines up
        // if `size` changes), then flexbox-centers `children` within it.
        // Deliberately not `transform: translate(-50%, -50%)` — verified
        // against the live map that a `transform` here made this element's
        // content silently fail to paint (while staying fully correct in
        // computed geometry/color, and in the DOM), leaving only the ring's
        // own SVG visible. This inset-box approach doesn't rely on
        // `transform` at all and renders correctly.
        <span
          className={styles.content}
          style={{
            left: `${(RING_INSET_X / VIEWBOX_WIDTH) * 100}%`,
            top: `${(RING_INSET_Y / VIEWBOX_HEIGHT) * 100}%`,
            width: `${(RING_SIZE / VIEWBOX_WIDTH) * 100}%`,
            height: `${(RING_SIZE / VIEWBOX_HEIGHT) * 100}%`,
          }}
        >
          {children}
        </span>
      ) : null}
    </span>
  );
};
